import { FC, SVGProps, useLayoutEffect, useRef, useState } from 'react'
import { ReactComponent as BookingIcon } from 'src/assets/svg/booking-brand.svg'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as GlobeIcon } from 'src/assets/svg/Globe.svg'
import { ReactComponent as GoogleIcon } from 'src/assets/svg/google.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as LinkedInIcon } from 'src/assets/svg/linkedin.svg'
import { ReactComponent as NewsAndBlogsIcon } from 'src/assets/svg/New-and-Blogs.svg'
import { ReactComponent as RedditIcon } from 'src/assets/svg/reddit.svg'
import { ReactComponent as TikTokIcon } from 'src/assets/svg/tiktok.svg'
import { ReactComponent as ThreadsIcon } from 'src/assets/svg/threads-square-svg.svg'
import { ReactComponent as TripadvisorIcon } from 'src/assets/svg/tripadvisor.svg'
import { ReactComponent as XIcon } from 'src/assets/svg/x.svg'
import { ReactComponent as WhatsappIcon } from 'src/assets/svg/whatsapp.svg'
import { ReactComponent as YouTubeIcon } from 'src/assets/svg/youtube.svg'
import { ReactComponent as TotalSourceIcon } from 'src/assets/svg/total-source-global.svg'
import { ReactComponent as ExpediaIcon } from 'src/assets/svg/expedia-brand.svg'
import { Skeleton } from 'src/components/Skeleton'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { SourcesModal } from './SourcesModal'
import styles from './totalSourcesCard.module.scss'

// .pill is 26px tall with a 12px radius and an 8px gap (totalSourcesCard.module
// .scss). Five pills is a typical first row-and-a-bit of platforms.
const PILL_HEIGHT = 26
const PILL_RADIUS = 12
const PILL_SKELETON_COUNT = 5

const MAX_ROWS = 3
const COLUMNS = 3
const MAX_VISIBLE = MAX_ROWS * COLUMNS

const SOURCE_ICON_MAP: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  booking: BookingIcon,
  expedia: ExpediaIcon,
  facebook: FacebookIcon,
  'google reviews': GoogleIcon,
  instagram: InstagramIcon,
  linkedin: LinkedInIcon,
  'news & blogs': NewsAndBlogsIcon,
  reddit: RedditIcon,
  threads: ThreadsIcon,
  tiktok: TikTokIcon,
  tripadvisor: TripadvisorIcon,
  twitter: XIcon,
  whatsapp: WhatsappIcon,
  youtube: YouTubeIcon,
}

const PILL_ROWS = 2

// booking/expedia brand svgs render black & white in this card
const MONO_BRAND_SOURCES = new Set(['booking', 'expedia'])
const monoIconClass = (source: string, base: string, monoClass: string) =>
  MONO_BRAND_SOURCES.has((source || '').toLowerCase())
    ? `${base} ${monoClass}`
    : base

interface SourcePillsProps {
  formatCount: (value: number) => string
  getIcon: (source: string) => FC<SVGProps<SVGSVGElement>>
  onMore: () => void
  sources: {
    name?: string
    total?: number
  }[]
}

const SourcePills: FC<SourcePillsProps> = ({
  formatCount,
  getIcon,
  onMore,
  sources,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(sources.length)
  const [measured, setMeasured] = useState(false)

  useLayoutEffect(() => {
    setVisibleCount(sources.length)
    setMeasured(false)
  }, [sources])

  useLayoutEffect(() => {
    if (measured || !containerRef.current) return

    const items = Array.from(containerRef.current.children) as HTMLElement[]
    const rowTops: number[] = []
    items.forEach((item) => {
      if (!rowTops.includes(item.offsetTop)) rowTops.push(item.offsetTop)
    })

    if (rowTops.length > PILL_ROWS) {
      const allowedTops = rowTops.slice(0, PILL_ROWS)
      const fitting = items.filter((item) =>
        allowedTops.includes(item.offsetTop)
      ).length
      // reserve one slot in the last row for the "+N" pill
      setVisibleCount(Math.max(fitting - 1, 1))
    }

    setMeasured(true)
  }, [measured, sources])

  const visible = sources.slice(0, visibleCount)
  const moreCount = sources.length - visible.length

  return (
    <div
      className={styles.pills}
      ref={containerRef}
      style={measured ? undefined : { visibility: 'hidden' }}
    >
      {visible.map((source) => {
        const PillIcon = getIcon(source.name ?? '')
        return (
          <div className={styles.pill} key={source.name}>
            <PillIcon
              className={monoIconClass(
                source.name ?? '',
                styles.pillIcon,
                styles.monoBrandIcon
              )}
            />
            <span className={styles.pillCount}>
              {formatCount(source.total ?? 0)}
            </span>
          </div>
        )
      })}
      {moreCount > 0 && (
        <button className={styles.morePill} onClick={onMore} type="button">
          + {moreCount}
        </button>
      )}
    </div>
  )
}

interface TotalSourcesCardProps {
  hasData?: boolean
  icon?: FC<SVGProps<SVGSVGElement>>
  isLoading?: boolean
  sourceCounts?: {
    name?: string
    total?: number
  }[]
  sources?: string[]
  uniqueCount?: number
}

export const TotalSourcesCard: FC<TotalSourcesCardProps> = ({
  hasData = false,
  icon: Icon = TotalSourceIcon,
  isLoading = false,
  sourceCounts,
  sources = [],
  uniqueCount,
}) => {
  const displayCount = uniqueCount ?? sources.length
  const [isModalOpen, setIsModalOpen] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const hasOverflow = sources.length > MAX_VISIBLE
  const visibleSources = hasOverflow
    ? sources.slice(0, MAX_VISIBLE - 1)
    : sources
  const moreCount = sources.length - visibleSources.length
  const columns: string[][] = [[], [], []]

  visibleSources.forEach((source, index) => {
    columns[index % COLUMNS].push(source)
  })

  const capitalize = (str: string) =>
    str.replace(/\b\w/g, (c) => c.toUpperCase())

  const getIcon = (source: string) => {
    return SOURCE_ICON_MAP[source.toLowerCase()] || GlobeIcon
  }

  const formatCount = (value: number) =>
    value.toLocaleString('en-US').replace(/,/g, ' ')

  return (
    <>
      <div className={styles.card} ref={cardRef}>
        <div className={styles.header}>
          <Icon className={styles.icon} />
          <h3 className={styles.title}>Total Sources</h3>
          {/* .totalCount is 24px. A "0" here while loading is a claim, not a
              value — the source list simply has not arrived yet. */}
          {isLoading ? (
            <span className={styles.totalCount}>
              <Skeleton height={24} width={32} />
            </span>
          ) : (
            hasData && <span className={styles.totalCount}>{displayCount}</span>
          )}
        </div>
        {isLoading ? (
          <div className={styles.content}>
            <div className={styles.pills}>
              {Array.from({ length: PILL_SKELETON_COUNT }).map((_, index) => (
                <Skeleton
                  height={PILL_HEIGHT}
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  radius={PILL_RADIUS}
                  width={64}
                />
              ))}
            </div>
          </div>
        ) : hasData ? (
          <div className={styles.content}>
            {sourceCounts ? (
              <SourcePills
                formatCount={formatCount}
                getIcon={getIcon}
                onMore={() => setIsModalOpen(true)}
                sources={sourceCounts}
              />
            ) : (
              <>
                <div className={styles.allSourcesLabel}>All Sources</div>
                <div className={styles.columns}>
                  {columns.map((column, colIndex) => (
                    <div className={styles.column} key={colIndex}>
                      {column.map((source) => {
                        const Icon = getIcon(source)
                        return (
                          <div className={styles.sourceItem} key={source}>
                            <Icon
                              className={monoIconClass(
                                source,
                                styles.sourceIcon,
                                styles.monoBrandIcon
                              )}
                            />
                            <TruncatedTooltip
                              className={styles.sourceName}
                              text={capitalize(source)}
                            />
                          </div>
                        )
                      })}
                      {hasOverflow && colIndex === COLUMNS - 1 && (
                        <div
                          className={styles.moreLink}
                          onClick={() => setIsModalOpen(true)}
                        >
                          + more {moreCount}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className={styles.noData}>No Data</div>
        )}
      </div>
      {isModalOpen && hasData && (
        <SourcesModal
          cardRef={cardRef}
          onClose={() => setIsModalOpen(false)}
          sources={sources}
        />
      )}
    </>
  )
}
